import { CheckCircleIcon, PaperclipIcon } from "@phosphor-icons/react";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import {
  GIFTPASS_HAND_PAYMENT_METHODS,
  GIFTPASS_PROOF_REQUIRED,
  type GiftpassHandPaymentMethod,
} from "@bednbody/types";
import { useState } from "react";
import { METHOD_LABEL } from "./giftpass-orders.model";
import { PROOF_ACCEPT, uploadProof } from "./giftpass-orders.hooks";

export interface PaymentValue {
  method: GiftpassHandPaymentMethod;
  reference: string;
  proofPath: string | null;
  proofName: string | null;
}

export const EMPTY_PAYMENT: PaymentValue = {
  method: "bank_transfer",
  reference: "",
  proofPath: null,
  proofName: null,
};

/** Enough to mark paid: a reference, and the proof when the method needs one (0375). */
export function paymentReady(value: PaymentValue): boolean {
  return (
    value.reference.trim() !== "" &&
    (!GIFTPASS_PROOF_REQUIRED.includes(value.method) || value.proofPath !== null)
  );
}

/** How it was paid, the reference, and the proof — shared by both ways of paying a manual order. */
export function PaymentFields({
  id,
  value,
  onChange,
}: {
  id: string;
  value: PaymentValue;
  onChange: (value: PaymentValue) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const proofRequired = GIFTPASS_PROOF_REQUIRED.includes(value.method);

  const attach = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const path = await uploadProof(file);
      onChange({ ...value, proofPath: path, proofName: file.name });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Couldn't attach the proof.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field>
        <FieldLabel htmlFor={`${id}-method`}>How was it paid?</FieldLabel>
        <NativeSelect
          id={`${id}-method`}
          value={value.method}
          onChange={(event) =>
            onChange({ ...value, method: event.target.value as GiftpassHandPaymentMethod })
          }
        >
          {GIFTPASS_HAND_PAYMENT_METHODS.map((m) => (
            <NativeSelectOption key={m} value={m}>
              {METHOD_LABEL[m]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-reference`}>Reference number</FieldLabel>
        <Input
          id={`${id}-reference`}
          type="text"
          maxLength={120}
          placeholder="Transaction or receipt no."
          value={value.reference}
          onChange={(event) => onChange({ ...value, reference: event.target.value })}
        />
      </Field>
      <Field className="col-span-2" data-invalid={uploadError ? true : undefined}>
        <FieldLabel htmlFor={`${id}-proof`}>
          Proof of payment{proofRequired ? "" : " (optional)"}
        </FieldLabel>
        <Input
          id={`${id}-proof`}
          type="file"
          accept={PROOF_ACCEPT}
          disabled={uploading}
          onChange={(event) => void attach(event.target.files?.[0])}
        />
        <FieldDescription>
          {uploading ? (
            "Attaching…"
          ) : uploadError ? (
            <span className="text-destructive">{uploadError}</span>
          ) : value.proofName ? (
            <span className="inline-flex items-center gap-1 text-success">
              <CheckCircleIcon weight="fill" className="size-4" /> {value.proofName} attached
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <PaperclipIcon className="size-4" /> A screenshot or photo of the transfer slip. JPG,
              PNG, WebP or PDF, up to 10 MB.
            </span>
          )}
        </FieldDescription>
      </Field>
    </div>
  );
}
